<template>
    <v-dialog v-model="dialog" max-width="900" @click:outside="hide">
        <v-card class="card-container">
            <v-card-title class="title-container">
                <v-icon color="#FFFFFF" @click="hide" large class="mr-3">mdi-close-circle-outline</v-icon>
                {{ $t('programs.assign_program') }}
            </v-card-title>
            <v-card-text class="mt-3">
                <v-row dense>
                    <v-col cols="12" md="6">
                        <v-row>
                            <v-col cols="12">
                                <set-selection-routine :coach-id="coachId" @change="onChangeSelection"
                                    @collection="collection = $event" ref="selection" />
                            </v-col>
                            <v-col cols="12">
                                <span class="label">{{ $t('programs.days_to_assign') }}</span>
                                <div class="row mx-auto justify-space-between">
                                    <v-checkbox v-for="(day, i) in days" :key="i" :label="day.name"
                                        v-model="days[i].active" hide-details color="secondary" />
                                </div>
                            </v-col>
                            <v-col cols="12">
                                <v-alert type="warning" border="left" outlined dense>
                                    {{ $t('programs.alert_replace_routines') }}
                                </v-alert>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" md="6" class="text-center">
                        <div class="subtitle-text">
                            {{ $t('programs.select_day_to_start_assign') }}
                        </div>
                        <mini-calendar :value="startDate" @change="startDate = $event" single force-value />
                    </v-col>
                </v-row>
            </v-card-text>
            <v-card-actions class="px-0 pb-0">
                <v-btn block depressed x-large color="primary" class="normal-btn no-radius black--text"
                    :disabled="disabled" @click="assign" :loading="loading">
                    {{ $t('common.assign') }}
                </v-btn>
            </v-card-actions>
        </v-card>
    </v-dialog>
</template>

<script>
import { mapMutations, mapState } from "vuex";
import { db, FieldValue } from "@/utils/Firebase";
import AssignRoutineSelector from "@/components/coach/routines/AssignRoutineSelector";
import KimsaCombobox from "@/components/common/inputs/KimsaCombobox";
import SetSelectionRoutine from './SetSelectionRoutine.vue';
import MiniCalendar from "@/components/common/MiniCalendar";
import {
    addAssignedRoutinesGroup,
    deleteAssignedRoutineStudentInDate,
    getPayloadBaseAssignedRoutine
} from "@/utils/Exercises";

export default {
    name: "AssignProgramDialog",
    components: { KimsaCombobox, AssignRoutineSelector, SetSelectionRoutine, MiniCalendar },
    props: {
        programId: {
            type: String,
            default: null,
        },
        coachId: {
            type: String,
            default: null
        }
    },
    data(vm) {
        return {
            dialog: false,
            program: null,
            loading: false,
            currentSelection: null,
            collection: null,
            startDate: null,

            daysTemplate: [
                { name: vm.$t('common.short_monday'), value: 1, active: false },
                { name: vm.$t('common.short_tuesday'), value: 2, active: false },
                { name: vm.$t('common.short_wednesday'), value: 3, active: false },
                { name: vm.$t('common.short_thursday'), value: 4, active: false },
                { name: vm.$t('common.short_friday'), value: 5, active: false },
                { name: vm.$t('common.short_saturday'), value: 6, active: false },
                { name: vm.$t('common.short_sunday'), value: 0, active: false },
            ],

            days: [
                { name: vm.$t('common.short_monday'), value: 1, active: false },
                { name: vm.$t('common.short_tuesday'), value: 2, active: false },
                { name: vm.$t('common.short_wednesday'), value: 3, active: false },
                { name: vm.$t('common.short_thursday'), value: 4, active: false },
                { name: vm.$t('common.short_friday'), value: 5, active: false },
                { name: vm.$t('common.short_saturday'), value: 6, active: false },
                { name: vm.$t('common.short_sunday'), value: 0, active: false },
            ],
        }
    },
    computed: {
        ...mapState(['dialogActive']),
        disabled(vm = this) {
            return !vm.currentSelection || !vm.startDate || vm.days.filter(day => day.active).length === 0
        },
        routinesToCopy(vm = this) {
            return vm.program?.assignedRoutines || []
        },
        routineQuantity(vm = this) {
            return vm.routinesToCopy?.length || 0
        },
        isGroup(vm = this) {
            return vm.collection === 'groupAssignedRoutines'
        },
    },
    methods: {
        ...mapMutations(['setDialogActive']),
        async show() {
            const vm = this
            vm.dialog = true
            vm.setDialogActive(true)
            await vm.loadProgram()
        },
        hide() {
            const vm = this
            vm.dialog = false
            vm.setDialogActive(false)
            vm.reset()
        },
        reset() {
            const vm = this
            vm.program = null
            if (vm.$refs.selection) vm.$refs.selection.reset()
            vm.collection = null
            vm.startDate = null
            vm.days = [...vm.daysTemplate]
        },
        async loadProgram() {
            let vm = this
            if (!vm.programId) return
            let programRef = await db.collection('programs').doc(vm.programId).get()
            vm.program = { ...programRef.data(), id: programRef.id }
        },
        onChangeSelection(selection) {
            const vm = this;
            vm.currentSelection = selection
        },
        daysToCopy() {
            const vm = this;
            let dates = [];
            let days = [...vm.days].filter(day => day.active).sort((a, b) => a.value - b.value);
            let currentDay = vm.$moment(vm.startDate);
            let indexDay = days.findIndex(day => currentDay.day() <= day.value);
            if (indexDay === -1) {
                currentDay.add(1, 'week');
                indexDay = 0;
            }
            for (let i = 0; i < vm.routineQuantity; i++) {
                dates.push(currentDay.day(days[indexDay].value).format('YYYY-MM-DD'));
                indexDay++;
                if (indexDay >= days.length) {
                    indexDay = 0;
                    currentDay.add(1, 'week');
                }
            }
            return dates;
        },
        async assign() {
            const vm = this
            vm.loading = true
            let range = vm.daysToCopy()
            let index = 0

            for (let date of range) {
                let dateFormatted = vm.$moment(date).format('DD-MM-YYYY')
                let assignedRoutine = {
                    ...vm.routinesToCopy[index],
                    coachId: vm.currentUser.uid,
                    img: vm.program?.img || '',
                    imgSavePath: vm.program?.imgSavePath || ''
                }

                let routineName = `${vm.currentSelection.name} - ${dateFormatted}`
                let payload = getPayloadBaseAssignedRoutine(routineName, date, assignedRoutine, assignedRoutine.blocks)

                if (vm.currentSelection.objectID) {
                    payload.studentId = vm.currentSelection.objectID
                    await deleteAssignedRoutineStudentInDate(vm.currentSelection.objectID, date)
                } else {
                    payload.groupId = vm.currentSelection.id
                    payload.teamId = vm.currentSelection.teamId || null
                }

                let assignedRoutineCreated = await db.collection(vm.collection).add(payload)

                if (vm.isGroup) {
                    for (let student of vm.currentSelection?.students || []) {
                        await deleteAssignedRoutineStudentInDate(student, date)
                    }
                    await addAssignedRoutinesGroup({ id: assignedRoutineCreated.id, ...payload, date: date }, vm.currentSelection.students)
                }

                index++
            }

            await db.collection('programs').doc(vm.program.id).update({ used: FieldValue.increment(index) })

            vm.loading = false
            vm.hide()
            vm.toast(vm.$t('programs.assigned_program'))
        },
    },
    watch: {
        dialogActive(val) {
            if (!val) this.hide()
        },
        programId() {
            this.loadProgram()
        },
    }
}
</script>

<style scoped>
.card-container {
    border-radius: 8px;
    overflow: hidden;
}

.title-container {
    color: #FFFFFF;
    background: var(--v-backGroundPrimary-base);
}

::v-deep .v-date-picker-table {
    border-bottom: none;
}
</style>